import { createScreenText } from 'helpers/screenText';
const st = createScreenText('issuance_plan');

export type IssuancePlanStatus = 1 | 2 | 3 | 4 | 5 | 6;

export const ISSUANCE_PLAN_STATUS = {
  Created: 1,
  Approved: 2,
  GenQr: 3,
  SentPrint: 4,
  Accept: 5,
  Cancel: 6,
} as const;

export interface IssuancePlan {
  id: number;
  planCode: string;
  expectDrawFormat?: string | null;
  ticketType: 'Nam' | 'Bac' | string;
  ticketTypeName?: string | null;
  ticketDesignTemplateId?: number | null;
  ticketDesignTemplateName?: string | null;
  expectedDrawDate: string;
  expectedPrintQuantity: number;
  numberRangeStart?: string | null;
  numberRangeEnd?: string | null;
  ticketSymbols: string[];
  status: IssuancePlanStatus | number;
  statusName?: string | null;
  approvedBy?: number | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  rejectionReason?: string | null;
  // Status truoc khi bi Huy (chi co gia tri khi status = Cancel). Approved/GenQr nghia la ke hoach
  // bi huy dung luc dang/da sinh ma QR ("Huy tao ve") — dung de hien nut "Duyet tao ve".
  cancelledFromStatus?: number | null;
  actualReceivedQuantity?: number | null;
  qrGenerationStatus: 'Generated' | 'NotGenerated' | string;
  canSendToPrint: boolean;
  // Mục 13.5, REQUIRE_FIXBUGS.md — true khi kế hoạch còn JobErrorLog chưa xử lý xong (bất kỳ loại
  // job nào). Dùng để hiện badge "Có lỗi" ở bảng danh sách chính.
  hasUnresolvedError?: boolean;
  createdAt?: string | null;
  createdByName?: string | null;
}

export interface IssuancePlanStatusConfig {
  statusCode: IssuancePlanStatus | number;
  statusName: string;
  description?: string | null;
  allowedNextStatuses: number[];
  displayOrder: number;
  badgeColor?: string | null;
  actionCode?: string | null;
  actionLabel?: string | null;
  isInitial: boolean;
  isFinal: boolean;
  isActive: boolean;
}

export interface CreateIssuancePlanPayload {
  ticketType: string;
  ticketDesignTemplateId: number;
  expectedDrawDate: string;
  expectedPrintQuantity: number;
  numberRangeStart: string;
  numberRangeEnd: string;
  ticketSymbols: string[];
}

export interface IssuancePlanFilters {
  status: string;
  month: string;
}

export interface IssuancePlanPagination {
  pageIndex: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export const TICKET_TYPE_OPTIONS = [
  { value: 'Nam', label: 'Mien Nam (5 so)', digits: 5 },
  { value: 'Bac', label: 'Mien Bac (6 so)', digits: 6 },
] as const;

export const DEFAULT_STATUS_LABELS: Record<string, string> = {
  1: st('cho_duyet_text'),
  2: st('sinh_ma_qr_text'),
  3: st('da_sinh_ma_qr_text'),
  4: st('da_gui_nha_in_text'),
  5: st('hoan_thanh_ke_hoach_text'),
  6: st('huy_ke_hoach_title'),
};

export const DEFAULT_BADGE_COLORS: Record<string, string> = {
  1: 'warning',
  2: 'info',
  3: 'success',
  4: 'primary',
  5: 'secondary',
  6: 'danger',
};

// Khớp với TicketGenerationRangeErrorStatuses enum bên backend (TicketService/Entities).
export const TICKET_GENERATION_RANGE_ERROR_STATUS = {
  Failed: 1,
  Resolved: 2,
} as const;

// Dải số bị lỗi khi sinh bộ số & QR chạy đa luồng (1 symbol hoặc 1 phần dải của 1 symbol) —
// dùng cho tab "Quản lý kế hoạch lỗi" (rerun lại riêng dải bị lỗi thay vì chạy lại toàn bộ job).
export interface TicketGenerationRangeError {
  id: number;
  jobId: string;
  lotteryPeriodId: number;
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
  errorMessage: string;
  status: 1 | 2;
  createdAt: string;
  resolvedAt?: string | null;
}

// 1 mốc trong lịch sử tác động lên 1 kế hoạch phát hành (tạo/duyệt/gửi in/nhận kho/hủy/duyệt lại
// tạo vé/hủy xuất vé/hủy xuất ảnh QR) — dùng để vẽ roadmap trong HistoryTimelineModal.
export interface IssuancePlanHistoryEntry {
  id: number;
  issuancePlanId: number;
  action: string;
  actionName?: string | null;
  fromStatus?: number | null;
  fromStatusName?: string | null;
  toStatus?: number | null;
  toStatusName?: string | null;
  reason?: string | null;
  actorId: number;
  actorName?: string | null;
  createdAt: string;
}

// Log lỗi tổng quát của job sinh QR (bao gồm cả lỗi "stalled" do StalledJobWatchdogService phát
// hiện) — dùng cho tab "Quản lý kế hoạch lỗi" (nguồn JobErrorLog, khác với TicketGenerationRangeError
// vốn chỉ ghi lỗi theo từng dải số cụ thể).
export interface JobErrorLogEntry {
  id: number;
  jobId: string;
  targetId: number;
  errorSource: string;
  errorMessage: string;
  occurredAt: string;
}
